import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardActions, 
  Avatar, 
  IconButton, 
  TextField, 
  Button, 
  Divider, 
  Grid, 
  Paper, 
  CircularProgress,
  Pagination,
  Stack,
  FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ThumbUpOutlined as ThumbUpIcon,
  ChatOutlined as CommentIcon,
  RepeatOutlined as ShareIcon,
  SendOutlined as SendIcon,
  PersonOutline as PersonIcon,
  Bookmark as BookmarkIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  Tag as TagIcon,
  WorkOutline as WorkIcon,
  EventNote as EventIcon,
  BarChart as AnalyticsIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../utils/supabaseClient';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  '&:hover': {
    boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
  }
}));

const SidebarCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2)
}));

const SidebarLink = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  cursor: 'pointer',
  borderRadius: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const postsPerPage = 20;

  const fetchPosts = async () => {
    setIsLoading(true);
    try {
      // First get total count
      const { count } = await supabase
        .from('posts')
        .select('*', { count: 'exact', head: true });

      setTotalPosts(count || 0);

      // Then fetch paginated posts
      const from = (page - 1) * postsPerPage;
      const to = from + (postsPerPage - 1);

      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false })
        .range(from, to);

      if (postsError) throw postsError;

      const userIds = [...new Set(postsData.map(post => post.user_id))];
      
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      const postsWithProfiles = postsData.map(post => ({
        ...post,
        profile: profilesData.find(profile => profile.id === post.user_id)
      }));

      setPosts(postsWithProfiles);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page]); // Refetch when page changes

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim()) return;

    try {
      const { error } = await supabase
        .from('posts')
        .insert([
          { content: newPostContent, user_id: user.id }
        ]);

      if (error) throw error;
      
      setNewPostContent('');
      fetchPosts();
    } catch (err) {
      console.error('Error creating post:', err);
      setError(err.message);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const trendingTopics = [
    { tag: 'javascript', count: '2.3k discussions' },
    { tag: 'react', count: '1.8k discussions' },
    { tag: 'nodejs', count: '1.2k discussions' },
    { tag: 'python', count: '950 discussions' }
  ];

  const networkStats = {
    connections: 1234,
    views: 567,
    searchAppearances: 89
  };

  const savedItems = [
    { title: 'My Items', icon: <BookmarkIcon /> },
    { title: 'My Groups', icon: <GroupsIcon /> },
    { title: 'My Events', icon: <EventIcon /> },
    { title: 'My Analytics', icon: <AnalyticsIcon /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Left Sidebar */}
        <Grid item xs={12} md={3}>
          {/* Profile Card */}
          <SidebarCard>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Avatar
                src={user?.user_metadata?.avatar_url}
                sx={{ width: 72, height: 72, margin: '0 auto', mb: 2 }}
              >
                {user?.user_metadata?.full_name?.[0] || <PersonIcon />}
              </Avatar>
              <Typography variant="h6">
                {user?.user_metadata?.full_name || 'Anonymous'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Software Developer
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Profile views: {networkStats.views}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Post impressions: {networkStats.searchAppearances}
              </Typography>
            </Box>
          </SidebarCard>

          {/* Saved Items */}
          <SidebarCard>
            {savedItems.map((item, index) => (
              <SidebarLink key={index}>
                {item.icon}
                <Typography variant="body2" sx={{ ml: 2 }}>
                  {item.title}
                </Typography>
              </SidebarLink>
            ))}
          </SidebarCard>
        </Grid>

        {/* Main Feed - keep existing feed code */}
        <Grid item xs={12} md={6}>
          {/* Create Post */}
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Avatar src={user?.user_metadata?.avatar_url}>
                  {user?.user_metadata?.full_name?.[0] || <PersonIcon />}
                </Avatar>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="What's on your mind?"
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  disabled={!newPostContent.trim() || isLoading}
                  onClick={handleCreatePost}
                  sx={{ textTransform: 'none' }}
                >
                  Post
                </Button>
              </Box>
            </CardContent>
          </StyledCard>

          {/* Posts Feed */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {posts.map((post) => (
                <StyledCard key={post.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Avatar src={post.profile?.avatar_url}>
                        {post.profile?.full_name?.[0] || <PersonIcon />}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1">
                          {post.profile?.full_name || 'Anonymous'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(post.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body1">{post.content}</Typography>
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Button
                      startIcon={<ThumbUpIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Like
                    </Button>
                    <Button
                      startIcon={<CommentIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Comment
                    </Button>
                    <Button
                      startIcon={<ShareIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Share
                    </Button>
                    <Button
                      startIcon={<SendIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Send
                    </Button>
                  </CardActions>
                </StyledCard>
              ))}

              {/* Pagination */}
              {totalPosts > postsPerPage && (
                <Stack spacing={2} sx={{ mt: 4, mb: 2, alignItems: 'center' }}>
                  <Pagination
                    count={Math.ceil(totalPosts / postsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        },
                      },
                      '& .Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Showing {(page - 1) * postsPerPage + 1} - {Math.min(page * postsPerPage, totalPosts)} of {totalPosts} posts
                  </Typography>
                </Stack>
              )}

              {posts.length === 0 && !isLoading && (
                <Box sx={{ textAlign: 'center', my: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No posts found
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Grid>

        {/* Right Sidebar */}
        <Grid item xs={12} md={3}>
          {/* Trending Topics */}
          <SidebarCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Trending Topics</Typography>
            </Box>
            {trendingTopics.map((topic, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                    cursor: 'pointer'
                  }
                }}
              >
                <TagIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    #{topic.tag}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {topic.count}
                  </Typography>
                </Box>
              </Box>
            ))}
          </SidebarCard>

          {/* Recent Activity */}
          <SidebarCard>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                startIcon={<WorkIcon />}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                Job Searches
              </Button>
              <Button
                startIcon={<GroupsIcon />}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                Groups
              </Button>
              <Button
                startIcon={<EventIcon />}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                Events
              </Button>
            </Box>
          </SidebarCard>

          {/* Follow Suggestions */}
          <SidebarCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">People to Follow</Typography>
              <IconButton size="small">
                <AddIcon />
              </IconButton>
            </Box>
            {[1, 2, 3].map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ width: 32, height: 32, mr: 1 }} />
                  <Box>
                    <Typography variant="body2">John Doe</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Senior Developer
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  Follow
                </Button>
              </Box>
            ))}
          </SidebarCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Feed;
